// src/edu-world/core/config-loader.ts
// Simple content config loader for the vertical slice (Phase 0)
// Loads JSON configs with basic field presence checks (no Zod yet).

import type { ZoneConfig, NPCConfig, Mission, MinigameConfig, WorldImpact } from '../types';

export interface VerticalSliceContent {
  zone: ZoneConfig;
  npcs: NPCConfig[];
  missions: Mission[];
  minigames: MinigameConfig[];
  impacts: WorldImpact[];
}

/**
 * Load a JSON file from the content directory.
 * In a browser/Vite context, this uses dynamic import with ?url or fetch.
 * For simplicity, we accept pre-imported JSON modules.
 */
export async function loadJson<T>(path: string): Promise<T> {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`[ConfigLoader] Failed to load "${path}": ${response.status}`);
  }
  return response.json() as Promise<T>;
}

/**
 * Simple validation: checks required fields exist.
 * Returns an error message or null if valid.
 */
function validateZone(zone: unknown): string | null {
  const z = zone as Record<string, unknown>;
  if (!z.id || !z.name || !z.width || !z.height || !z.spawnPoint) {
    return 'ZoneConfig missing required fields (id, name, width, height, spawnPoint)';
  }
  return null;
}

function validateNPC(npc: unknown): string | null {
  const n = npc as Record<string, unknown>;
  if (!n.id || !n.name || !n.position || !n.zone_id || !n.interaction_radius) {
    return `NPCConfig missing required fields (id, name, position, zone_id, interaction_radius)`;
  }
  return null;
}

function validateMission(mission: unknown): string | null {
  const m = mission as Record<string, unknown>;
  if (!m.id || !m.npc_id || !m.minigame_link || !m.world_impact_ref) {
    return 'Mission missing required fields (id, npc_id, minigame_link, world_impact_ref)';
  }
  return null;
}

function validateMinigame(minigame: unknown): string | null {
  const mg = minigame as Record<string, unknown>;
  if (!mg.id || !mg.mechanic_type || mg.difficulty === undefined) {
    return 'MinigameConfig missing required fields (id, mechanic_type, difficulty)';
  }
  return null;
}

function validateImpact(impact: unknown): string | null {
  const i = impact as Record<string, unknown>;
  if (!i.id || !i.type || !i.target_element || !i.visual_change) {
    return 'WorldImpact missing required fields (id, type, target_element, visual_change)';
  }
  return null;
}

/**
 * Load the vertical slice content from pre-imported JSON objects.
 * Used when JSON is imported at build time via Vite's ?url or direct import.
 */
export function loadVerticalSliceFromObjects(data: {
  zone: unknown;
  npcs: unknown[];
  missions: unknown[];
  minigames: unknown[];
  impacts: unknown[];
}): VerticalSliceContent {
  // Validate zone
  const zoneErr = validateZone(data.zone);
  if (zoneErr) {
    console.error(`[ConfigLoader] ${zoneErr}`);
    throw new Error(zoneErr);
  }

  // Validate NPCs
  const validNpcs: NPCConfig[] = [];
  for (const npc of data.npcs) {
    const err = validateNPC(npc);
    if (err) {
      console.warn(`[ConfigLoader] Skipping NPC: ${err}`);
      continue;
    }
    validNpcs.push(npc as NPCConfig);
  }

  // Validate missions
  const validMissions: Mission[] = [];
  for (const mission of data.missions) {
    const err = validateMission(mission);
    if (err) {
      console.warn(`[ConfigLoader] Skipping mission: ${err}`);
      continue;
    }
    // Default state to 'available' if not set
    const m = mission as Mission;
    if (!m.state) m.state = 'available';
    validMissions.push(m);
  }

  // Validate minigames
  const validMinigames: MinigameConfig[] = [];
  for (const mg of data.minigames) {
    const err = validateMinigame(mg);
    if (err) {
      console.warn(`[ConfigLoader] Skipping minigame: ${err}`);
      continue;
    }
    validMinigames.push(mg as MinigameConfig);
  }

  // Validate impacts
  const validImpacts: WorldImpact[] = [];
  for (const impact of data.impacts) {
    const err = validateImpact(impact);
    if (err) {
      console.warn(`[ConfigLoader] Skipping impact: ${err}`);
      continue;
    }
    validImpacts.push(impact as WorldImpact);
  }

  return {
    zone: data.zone as ZoneConfig,
    npcs: validNpcs,
    missions: validMissions,
    minigames: validMinigames,
    impacts: validImpacts,
  };
}
